import { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchCruiseSearchCount } from 'actions/CruiseActions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import getObjectKey from 'lib/object/getObjectKey'
import {
  buildSimilarCruiseSearchCandidates,
  resolveCruisePortFacet,
  selectSimilarCruiseSearchCandidate,
} from 'lib/cruises/cruiseSoldOutSimilarSearchUtils'
import useCruiseSearchFacets from './useCruiseSearchFacets'

interface Props {
  arrivalDate?: string | null
  cabinType: string
  departureDate?: string | null
  offer: App.CruiseOffer
}

interface SimilarCruiseSearchUrl {
  isResolving: boolean
  navigateToSimilarCruises: () => void
}

const SIMILAR_CRUISE_PORT_FACET_PARAMS: App.CruiseSearchFacetParams = {
  facetTypes: ['departure_ports', 'destination_ports'],
}

function useSimilarCruiseSearchUrl({
  arrivalDate,
  cabinType,
  departureDate,
  offer,
}: Props): SimilarCruiseSearchUrl {
  const dispatch = useAppDispatch()
  const navigateTo = useNavigateTo()
  const [isResolving, setIsResolving] = useState(false)
  const [cruiseFacets, fetchingCruiseFacets] = useCruiseSearchFacets(
    SIMILAR_CRUISE_PORT_FACET_PARAMS,
    { isDisabled: !isResolving },
  )

  const departurePlace = useMemo(
    () => resolveCruisePortFacet(offer.departurePort, cruiseFacets.departurePorts),
    [cruiseFacets.departurePorts, offer.departurePort],
  )
  const destinationPlace = useMemo(
    () => resolveCruisePortFacet(offer.returnPort, cruiseFacets.destinationPorts),
    [cruiseFacets.destinationPorts, offer.returnPort],
  )

  const candidates = useMemo(() => buildSimilarCruiseSearchCandidates({
    arrivalDate,
    cabinType,
    cruiseLineCode: offer.cruiseLine.code,
    departureDate,
    departurePlace,
    destinationPlace,
    offer,
  }), [
    arrivalDate,
    cabinType,
    departureDate,
    departurePlace,
    destinationPlace,
    offer,
  ])

  const candidateKeys = useMemo(
    () => Object.values(candidates).map(candidate => getObjectKey(candidate.filters)),
    [candidates],
  )

  const countsByKey = useAppSelector(state => {
    const counts: Record<string, number | undefined> = {}
    candidateKeys.forEach(key => {
      const countState = state.cruise.cruiseSearchCount[key]
      counts[key] = countState?.fetching ? undefined : countState?.count
    })
    return counts
  }, shallowEqual)

  const selection = useMemo(() => selectSimilarCruiseSearchCandidate(
    candidates,
    filters => countsByKey[getObjectKey(filters)],
  ), [candidates, countsByKey])

  const navigateToSimilarCruises = useCallback(() => {
    setIsResolving(true)
  }, [])

  useEffect(() => {
    if (!isResolving) return
    if (fetchingCruiseFacets) return

    if (selection.pendingCandidate) {
      dispatch(fetchCruiseSearchCount(selection.pendingCandidate.filters))
      return
    }

    navigateTo(selection.candidate.to)
    setIsResolving(false)
  }, [dispatch, fetchingCruiseFacets, isResolving, navigateTo, selection])

  return {
    isResolving,
    navigateToSimilarCruises,
  }
}

export default useSimilarCruiseSearchUrl
